import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { defineMessages } from 'react-intl';

import type { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { useEventsContext } from '@sorare/core/src/contexts/events';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { Sum } from '@sorare/core/src/hooks/useMonetaryAmount';
import type { MonetaryAmount } from '@sorare/core/src/lib/monetaryAmount';
import { ClickAddMultipleToCart_Origin } from '@sorare/core/src/protos/events/so5/shared/events';

import { useMarketplaceContext } from '#src/contexts/Marketplace/index.tsx';
import { useCart } from '#src/hooks/useCart.ts';

import type {
  useAddCollectionToCart_cardCollection,
  useAddCollectionToCart_userCardCollection,
} from './__generated__/useAddCollectionToCart.graphql.ts';
import { useFilterLivePrimaryBuys } from './useFilterLivePrimaryBuys.ts';

type Props<
  T extends useAddCollectionToCart_userCardCollection,
  U extends useAddCollectionToCart_cardCollection,
> = {
  userCardCollection: Nullable<T>;
  cardCollection: Nullable<U>;
  sport: Sport;
};

const isSameCollectionCard = <
  T extends useAddCollectionToCart_userCardCollection,
  U extends useAddCollectionToCart_cardCollection,
>(
  card: {
    anyPlayer: { slug: string };
    rarityTyped: Rarity;
    anyTeam?: { slug: string };
    seasonYear: number;
  },
  userCardCollection: Nullable<T>,
  cardCollection: Nullable<U>
) => {
  if (!userCardCollection) return false;
  return userCardCollection.slots.some(
    slot =>
      slot.slot.anyPlayer.slug === card.anyPlayer.slug &&
      card.rarityTyped === cardCollection?.rarity &&
      card.anyTeam?.slug === cardCollection?.team?.slug &&
      card.seasonYear === cardCollection?.season?.startYear
  );
};

const errorMessages = defineMessages({
  completeCollectionAlreadyInCart: {
    defaultMessage:
      'You have already added all the available cards to your cart to complete the collection',
  },
  notEnoughCardsToCompleteCollection: {
    defaultMessage:
      'There are not enough cards available on Instant Buy to complete the collection right now. Check back soon!',
  },
  maxLevelReached: {
    defaultMessage:
      'Congrats! You’ve already reached the highest collection bonus level for this club',
  },
  levelUpAlreadyInCart: {
    defaultMessage: 'You have already added cards to your cart to level up',
  },
  notEnoughCardsToLevelUp: {
    defaultMessage:
      'There are not enough cards available on Instant Buy to level up right now. Check back soon!',
  },
});

export const useAddCollectionToCart = <
  T extends useAddCollectionToCart_userCardCollection,
  U extends useAddCollectionToCart_cardCollection,
>({
  userCardCollection,
  cardCollection,
  sport,
}: Props<T, U>) => {
  const { formatMessage } = useIntlContext();
  const { showAddToCartPopin } = useMarketplaceContext();
  const { track } = useEventsContext();
  const { addCardsToCart, cart } = useCart();
  const { addCardsToCart: addNextStepCardsToCart } = useCart();

  const marketCards =
    userCardCollection?.slots
      .map(slot => slot.slot.anyPlayer.anyCardWithLivePrimaryOffer)
      .filter(Boolean) || [];
  const cardsInCart = cart?.mainCartItems.map(item => item.card) || [];

  const sameCollectionCardsInCart = cardsInCart.filter(card =>
    isSameCollectionCard(card, userCardCollection, cardCollection)
  );

  const missingSlots =
    userCardCollection?.slots.filter(slot => !slot.cardCollectionCardsCount) ||
    [];

  const missingCards =
    marketCards
      ?.filter(card =>
        missingSlots.some(
          slot => slot.slot.anyPlayer.slug === card.anyPlayer.slug
        )
      )
      .filter(card =>
        isSameCollectionCard(card, userCardCollection, cardCollection)
      ) || [];

  const missingCardsWithoutCardsInCart = missingCards.filter(
    card =>
      !sameCollectionCardsInCart.some(
        c => c.anyPlayer.slug === card.anyPlayer.slug
      )
  );

  const missingCardsWithLivePrimaryOffer = useFilterLivePrimaryBuys(
    missingCardsWithoutCardsInCart
  );

  const unavailableSlots = missingSlots?.filter(
    slot =>
      !missingCardsWithoutCardsInCart.some(
        c => c.anyPlayer.slug === slot.slot.anyPlayer.slug
      )
  );

  const sortedMissingCards = missingCardsWithLivePrimaryOffer.sort((a, b) => {
    const aPrice = a.monetaryAmount.eurCents;
    const bPrice = b.monetaryAmount.eurCents;
    return aPrice - bPrice;
  });

  const totalPrice = Sum(sortedMissingCards.map(card => card.monetaryAmount));

  const trackAddMultipleToCart = (
    card_slugs: string[],
    offer_ids: string[],
    price: MonetaryAmount,
    origin: ClickAddMultipleToCart_Origin
  ) => {
    track('Click Add Multiple To Cart', {
      cartId: cart?.id,
      card_slugs,
      collection_slug: cardCollection?.slug,
      scarcity: cardCollection?.rarity,
      offer_ids,
      eth_amount: price.eth,
      eur_amount: price.eur,
      origin,
      sport,
    });
  };

  const addMissingCardsToCart = (() => {
    const { disabled, disableReason } = (() => {
      const allMissingCardsInCart =
        missingCards.length > 0 &&
        missingCards.every(
          mc =>
            !!sameCollectionCardsInCart.find(
              c => c.anyPlayer.slug === mc.anyPlayer.slug
            )
        );
      if (allMissingCardsInCart) {
        return {
          disabled: true,
          disableReason: formatMessage(
            errorMessages.completeCollectionAlreadyInCart
          ),
        };
      }
      if (sortedMissingCards.length === 0)
        return {
          disabled: true,
          disableReason: formatMessage(
            errorMessages.notEnoughCardsToCompleteCollection
          ),
        };
      return { disabled: false, disableReason: undefined };
    })();

    const missingSlugs = sortedMissingCards.map(({ card }) => card.slug);
    const offerIds = sortedMissingCards
      .map(({ card }) => card.livePrimaryOffer?.id)
      .filter(Boolean);
    return {
      mutate: async () => {
        trackAddMultipleToCart(
          missingSlugs,
          offerIds,
          totalPrice,
          ClickAddMultipleToCart_Origin.COMPLETE_COLLECTION
        );
        const c = await addCardsToCart.mutate(sport, missingSlugs);
        const addedToCartSlugs =
          c?.mainCartItems?.map(item => item.card.slug) || [];
        showAddToCartPopin(addedToCartSlugs);
      },
      loading: addCardsToCart.loading,
      disabled,
      disableReason,
    };
  })();

  const currentScoreWithCardsInCart = sameCollectionCardsInCart.reduce(
    (acc, curr) =>
      acc + (curr.collectionIncreaseScoreBreakdownAfterTransfer?.total || 0),
    userCardCollection?.score || 0
  );

  const nextThreshold = (() => {
    const threshold = [...(cardCollection?.bonusThresholds || [])]
      .sort((a, b) => {
        return a.threshold - b.threshold;
      })
      .find(t => t.threshold > (userCardCollection?.score || 0));
    return threshold;
  })();

  const missingCardsToNextThresholds = sortedMissingCards.reduce<{
    scoreIncreased: number;
    missingCards: typeof sortedMissingCards;
  }>(
    (acc, curr) => {
      const increaseScore =
        curr.card.collectionIncreaseScoreBreakdownAfterTransfer?.total || 0;
      const scoreIncreased = acc.scoreIncreased + increaseScore;

      if (
        !!increaseScore &&
        !!nextThreshold &&
        acc.scoreIncreased < nextThreshold.threshold
      ) {
        return {
          scoreIncreased,
          missingCards: [...acc.missingCards, curr],
        };
      }
      return acc;
    },
    {
      scoreIncreased: currentScoreWithCardsInCart,
      missingCards: [],
    }
  );

  const totalPriceToNextThresholds = Sum(
    missingCardsToNextThresholds.missingCards.map(card => card.monetaryAmount)
  );

  const addMissingCardsToNextThresholdToCart = (() => {
    const mc = missingCardsToNextThresholds.missingCards;
    const missingSlugs = mc.filter(Boolean).map(({ card }) => card.slug);

    const { disabled, disableReason } = (() => {
      if (!nextThreshold) {
        return {
          disabled: true,
          disableReason: formatMessage(errorMessages.maxLevelReached),
        };
      }
      if (currentScoreWithCardsInCart >= nextThreshold.threshold) {
        return {
          disabled: true,
          disableReason: formatMessage(errorMessages.levelUpAlreadyInCart),
        };
      }
      if (
        missingCardsToNextThresholds.scoreIncreased < nextThreshold.threshold
      ) {
        return {
          disabled: true,
          disableReason: formatMessage(errorMessages.notEnoughCardsToLevelUp),
        };
      }
      return { disabled: false, disableReason: undefined };
    })();

    return {
      mutate: async () => {
        trackAddMultipleToCart(
          missingSlugs,
          mc.map(({ card }) => card.livePrimaryOffer?.id).filter(Boolean),
          totalPriceToNextThresholds,
          ClickAddMultipleToCart_Origin.NEXT_STEP_COLLECTION
        );
        await addNextStepCardsToCart.mutate(sport, missingSlugs).then(c => {
          const slugs = c?.mainCartItems?.map(item => item.card.slug) || [];
          showAddToCartPopin(slugs.filter(s => missingSlugs.includes(s)));
        });
      },
      loading: addNextStepCardsToCart.loading,
      disabled,
      disableReason,
    };
  })();

  type GenericCardType = NonNullable<
    T['slots'][number]['slot']['anyPlayer']['anyCardWithLivePrimaryOffer']
  >;
  return {
    addMissingCardsToCart,
    addMissingCardsToNextThresholdToCart,
    unavailableSlots,
    missingCards: sortedMissingCards.map(c => c.card) as GenericCardType[],
    totalPrice,
    missingCardsToNextThresholds: missingCardsToNextThresholds.missingCards.map(
      c => c.card
    ) as GenericCardType[],
    totalPriceToNextThresholds,
  };
};

useAddCollectionToCart.fragments = {
  userCardCollection: gql`
    fragment useAddCollectionToCart_userCardCollection on UserCardCollection {
      id
      score
      slots(skins: $skins) {
        slug
        cardCollectionCardsCount
        slot {
          id
          anyPlayer {
            slug
            anyCardWithLivePrimaryOffer(rarity: $rarity) {
              slug
              rarityTyped
              anyTeam {
                slug
              }
              anyPlayer {
                slug
              }
              seasonYear
              collectionIncreaseScoreBreakdownAfterTransfer {
                total
              }
              ...useFilterLivePrimaryBuys_anyCard
            }
          }
        }
      }
    }
  ` as TypedDocumentNode<useAddCollectionToCart_userCardCollection>,
  cardCollection: gql`
    fragment useAddCollectionToCart_cardCollection on CardCollection {
      slug
      rarity
      bonusThresholds {
        threshold
      }
      season {
        startYear
      }
      team: anyTeam {
        slug
        name
      }
    }
    ${useFilterLivePrimaryBuys.fragments.anyCard}
  ` as TypedDocumentNode<useAddCollectionToCart_cardCollection>,
};
